import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal, type Signal } from '@angular/core';
import { Router } from '@angular/router';
import { isPhotoBookApproved, isUsableAlbum, masterIdOf, type FC_PhotoBookDTO } from '@lite-workspace/dto-fotoclass-book';
import type { AlbumEditorFacade } from '@lite-workspace/album-editor';
import { ALBUM_KIND, isTrumeauKind } from '@lite-workspace/dto-design-system';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { AlbumEditorReuseStrategy } from '../../../../core/album-editor-reuse.strategy';
import { AlbumGenerationTrackerService } from '../../../../core/album-generation-tracker.service';
import {
  BookAssemblyApiService,
  type ProductGeometryOptions,
  type ProductGeometrySummary,
} from '../infrastructure/book-assembly-api.service';
import type { PrintProductEntry } from '../infrastructure/print-services-api.service';
import { showTemplateDonorRefusal } from '../utils/template-donor-refusal-toast';
import { PrintProductFormatService, printProductsCompatible } from './print-product-format.service';
import { ProjectsFacade } from './projects.facade';

const RUNNING = 'Обновляем альбомы под новый продукт';
const EDITOR_RELOADS = 'Когда закончим, редактор обновится.';
const DONE = 'Альбомы обновлены. Проверьте каждый перед печатью.';
const FAILED = 'Не все альбомы обновились';
const RETRY = 'Повторить';
/** Another run (apply-design or a product change, any tab) holds `apply-run`: the product change waits for it. */
export const APPLY_RUN_BUSY = 'Сменить продукт можно, когда альбомы обновятся';
/** Skip reason of the geometry job: album sits on another sheet, only a rebuild catches it up. */
const FORMAT_CHANGED = 'format-changed';

/** Raised curtain of the open master editor: takes the rebuilt model in place. */
export type RebuildCurtain = Pick<AlbumEditorFacade, 'reloadAlbum' | 'cancelRebuild'>;

/** What the master hand-off left for the end of the run. */
interface MasterHandoff {
  curtainTaken: boolean;
  /** Master open in its editor without a curtain: reloaded only after the students. */
  reloadAtEnd: string | null;
  /** A retry never rebuilds it again: the photographer may already be editing it. */
  masterBuilt: boolean;
}

export interface ApplyToAllAlbumsOptions {
  /** Applied pack the format gate saw conflict with the new product — dropped on every rebuild. */
  resetPackId?: string;
  rebuildMaster: boolean;
  /** Called at the start of every run that rebuilds the master, retries included. */
  raiseCurtain?: () => RebuildCurtain | null;
  /** Albums of the changed item (its etalon included); absent = every album of the project. */
  albumIds?: readonly string[];
  /** The changed item: its etalon = `masterIdOf(settings, itemProductId)`; absent = the main item. */
  itemProductId?: string;
}

/** Продукт, на котором собран альбом: лёгкий список несёт его своим полем, полная карточка — в настройках. */
export function albumProductId(album: FC_PhotoBookDTO): string | null | undefined {
  return album.printProductId ?? album.settings?.photoBook?.printProductId;
}

export interface AlbumPlan {
  master: FC_PhotoBookDTO | null;
  /** Approved students: their approval is revoked before the write. */
  approved: FC_PhotoBookDTO[];
  /** Students on a sheet the next product cannot take by scale. */
  rebuild: FC_PhotoBookDTO[];
  move: FC_PhotoBookDTO[];
  /** Albums (master included) of the other kind: their own spread list is the old kind's, never reused. */
  restructure: FC_PhotoBookDTO[];
}

/** NULL kind = альбом. */
export function productKindChanged(current: PrintProductEntry | null | undefined, next: PrintProductEntry): boolean {
  return (current?.productKind || ALBUM_KIND) !== (next.productKind || ALBUM_KIND);
}

/** Another kind, or another трюмо row: its fold bands are its own, the panels rebuild. */
export function productStructureChanged(current: PrintProductEntry, next: PrintProductEntry): boolean {
  // ponytail: the entry carries no fold bands, so any other трюмо row counts as new bands.
  return productKindChanged(current, next) || (isTrumeauKind(next.productKind) && current.id !== next.id);
}

/** The one master/student split and compatibility rule: the confirm dialog counts it, the run executes it. */
export async function planAlbums(
  albums: readonly FC_PhotoBookDTO[],
  masterId: string | null,
  next: PrintProductEntry,
  productById: (id: string | null | undefined) => Promise<PrintProductEntry | null>,
): Promise<AlbumPlan> {
  // The master may carry its donor's personId.
  const isMaster = (album: FC_PhotoBookDTO): boolean => album.personId === null || album.id === masterId;
  const students = albums.filter(album => !isMaster(album));
  // Unknown album product = fail open: the geometry job judges it and skips it as format-changed.
  const sheets = await Promise.all(albums.map(album => productById(albumProductId(album))));
  const sheetOf = new Map(albums.map((album, index) => [album, sheets[index]]));
  // A structure change is never a geometry move: every such student rebuilds.
  const restructure = albums.filter(album => {
    const sheet = sheetOf.get(album);
    return !!sheet && productStructureChanged(sheet, next);
  });
  const rebuild = students.filter(album => {
    const sheet = sheetOf.get(album);
    return restructure.includes(album) || (!!sheet && !printProductsCompatible(sheet, next));
  });
  return {
    master: albums.find(isMaster) ?? null,
    approved: students.filter(album => isPhotoBookApproved(album)),
    rebuild,
    move: students.filter(album => !rebuild.includes(album)),
    restructure,
  };
}

/**
 * Перевод всех альбомов проекта на новый продукт после подтверждения фотографа. Порядок как у
 * `VignetteRefreshService`: утверждение снимается до записи (сервер утверждённый не перепишет), эталон
 * первым — ученики копируют его структуру. Совместимые ученики перекладываются заданием сборщика, ученики
 * на другом листе пересобираются. Смена проекта на экране прогон не прерывает: настройки уже сохранены.
 */
@Injectable({ providedIn: 'root' })
export class ProductGeometryRefreshService {
  private readonly assembly = inject(BookAssemblyApiService);
  private readonly facade = inject(ProjectsFacade);
  private readonly formats = inject(PrintProductFormatService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly editorReuse = inject(AlbumEditorReuseStrategy);
  private readonly document = inject(DOCUMENT);
  private readonly tracker = inject(AlbumGenerationTrackerService);
  private readonly running = signal<ReadonlyMap<string, Promise<void>>>(new Map());
  /** Projects whose `apply-run` lock this tab holds. */
  private readonly heldLocks = new Set<string>();

  // jsdom forbids stubbing location.reload — the seam exists for the spec alone.
  reloadEditor = (): void => this.document.defaultView?.location.reload();

  constructor() {
    // Same page-lifetime lock as ApplyDesignService: a reload kills the run, so its lock goes with the page.
    const view = this.document.defaultView;
    view?.addEventListener('pagehide', () => {
      for (const projectId of this.heldLocks) this.tracker.endSweep(projectId, 'apply-run');
    });
    view?.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      for (const projectId of this.heldLocks) this.tracker.beginSweep(projectId, 'apply-run');
    });
  }

  busy(projectId: string): Signal<boolean> {
    return computed(() => this.running().has(projectId));
  }

  /** Single-flight per project: a second call during a run joins it. */
  applyToAllAlbums(projectId: string, next: PrintProductEntry, opts: ApplyToAllAlbumsOptions): Promise<void> {
    const current = this.running().get(projectId);
    if (current) return current;
    const task = this.apply(projectId, next, opts).finally(() => {
      const rest = new Map(this.running());
      rest.delete(projectId);
      this.running.set(rest);
    });
    this.running.set(new Map(this.running()).set(projectId, task));
    return task;
  }

  run(projectId: string, printProductId: string, options: ProductGeometryOptions): Promise<ProductGeometrySummary> {
    return this.assembly.applyProductGeometry(projectId, printProductId, options);
  }

  private async apply(projectId: string, next: PrintProductEntry, opts: ApplyToAllAlbumsOptions): Promise<void> {
    // Another run (any tab) holds the project: running beside it, either endSweep would clear the other's lock.
    // Checked synchronously with updateAlbums' beginSweep: no await between them.
    // The product is already saved here: the refusal keeps the retry, the albums still owe the run.
    if (this.tracker.isSweeping(projectId, 'apply-run')) {
      this.toast.action(FAILED, RETRY, () => void this.applyToAllAlbums(projectId, next, opts), 'error');
      return;
    }

    const settings = this.facade.baseSettingsByProject()[projectId];
    // The settings write before the run may already have re-keyed the item etalon to the new product.
    const masterId = masterIdOf(settings, opts.itemProductId) ?? (opts.itemProductId ? masterIdOf(settings, next.id) : null);
    const inMasterEditor = (): boolean => !!masterId && this.router.url.split('?')[0] === `/editor/${projectId}/${masterId}`;
    const curtain = opts.rebuildMaster ? (opts.raiseCurtain?.() ?? null) : null;
    // Only the uncurtained master editor reloads at the run end; a curtain hands the model over early.
    const reloadsAtEnd = opts.rebuildMaster && !curtain && inMasterEditor();
    const busyToast = this.toast.loading(RUNNING, reloadsAtEnd ? EDITOR_RELOADS : undefined);
    const handoff: MasterHandoff = { curtainTaken: false, reloadAtEnd: null, masterBuilt: false };
    let ok = false;
    try {
      ok = await this.updateAlbums(projectId, next, opts, curtain, masterId, inMasterEditor, handoff);
    } catch {
      ok = false;
    } finally {
      this.toast.dismiss(busyToast);
    }

    if (ok) this.toast.success(DONE);
    else {
      const retry = { ...opts, rebuildMaster: opts.rebuildMaster && !handoff.masterBuilt };
      this.toast.action(FAILED, RETRY, () => void this.applyToAllAlbums(projectId, next, retry), 'error');
    }

    if (handoff.reloadAtEnd !== null) {
      if (inMasterEditor()) this.reloadEditor();
      else this.editorReuse.forget(projectId, handoff.reloadAtEnd);
    }

    if (!handoff.curtainTaken) curtain?.cancelRebuild();
  }

  private async updateAlbums(
    projectId: string,
    next: PrintProductEntry,
    opts: ApplyToAllAlbumsOptions,
    curtain: RebuildCurtain | null,
    masterId: string | null,
    inMasterEditor: () => boolean,
    handoff: MasterHandoff,
  ): Promise<boolean> {
    // The lock hides the etalon bands; markActive badges the card without raising the «альбом N из M» banner total.
    this.tracker.beginSweep(projectId, 'apply-run');
    this.heldLocks.add(projectId);
    // Started but not rebuilt: whatever the run never finished leaves the tracker with it.
    const pending = new Set<string>();

    const turn = (albumId: string): void => {
      pending.add(albumId);
      this.tracker.markActive(projectId, albumId);
      this.tracker.touchSweep(projectId, 'apply-run');
      this.facade.markAlbumsRegenerating(projectId, [albumId]);
    };

    try {
      await this.facade.loadAlbums(projectId);
      const scope = opts.albumIds ? new Set(opts.albumIds) : null;
      const albums = (this.facade.albumsByProject()[projectId] ?? []).filter(
        album => isUsableAlbum(album) && (!scope || scope.has(album.id)),
      );
      const { master, approved, restructure, ...plan } = await planAlbums(albums, masterId, next, id =>
        this.formats.productById(id),
      );
      let { rebuild, move } = plan;
      const fresh = (album: FC_PhotoBookDTO): { freshStructure?: true } =>
        restructure.includes(album) ? { freshStructure: true } : {};
      let ok = true;

      if (approved.length > 0) {
        const ids = approved.map(album => album.id);
        const rejected = new Set((await this.facade.revokeApproval(projectId, ids)).rejected.map(item => item.id));
        if (rejected.size > 0) {
          ok = false;
          rebuild = rebuild.filter(album => !rejected.has(album.id));
          move = move.filter(album => !rejected.has(album.id));
        }
      }

      const rebuiltMaster = opts.rebuildMaster ? master : null;

      if (master && rebuiltMaster) {
        turn(master.id);
        const built = await this.rebuildMaster(projectId, master, { ...fresh(master), resetPackId: opts.resetPackId }).catch(
          () => false,
        );
        if (built) pending.delete(master.id);
        handoff.masterBuilt = built;
        await this.handOffMaster(projectId, master.id, built, curtain, inMasterEditor(), handoff);
        if (!built) return false;
      }

      if (move.length > 0) {
        const photobookIds = move.map(album => album.id);
        const summary = await this.run(projectId, next.id, {
          includeApproved: true,
          photobookIds,
          itemProductId: opts.itemProductId,
        });
        for (const skip of summary.skipped ?? []) {
          const album = move.find(item => item.id === skip.photobookId);
          if (album && skip.reason === FORMAT_CHANGED) rebuild.push(album);
          else ok = false;
        }
      }

      for (const album of rebuild) {
        turn(album.id);
        const done = await this.facade.generateAlbum(
          projectId,
          { id: album.personId as string },
          {
            structureFromProject: true,
            quiet: true,
            ...fresh(album),
            ...(opts.resetPackId ? { resetDesignPack: opts.resetPackId } : {}),
          },
        );
        if (done) pending.delete(album.id);
        else ok = false;
      }

      await this.facade.loadAlbums(projectId);
      return ok;
    } finally {
      // completeAlbum, not completeRun: the run never added to `total`, so it gives nothing back.
      for (const albumId of pending) this.tracker.completeAlbum(projectId, albumId);
      this.heldLocks.delete(projectId);
      this.tracker.endSweep(projectId, 'apply-run');
    }
  }

  /** Шаблон проекта (personId === null) своего ученика не имеет — материал даёт донор, цель задаёт targetMaster. */
  private async rebuildMaster(
    projectId: string,
    master: FC_PhotoBookDTO,
    { resetPackId, freshStructure }: { resetPackId?: string; freshStructure?: boolean },
  ): Promise<boolean> {
    if (isPhotoBookApproved(master)) {
      const { rejected } = await this.facade.revokeApproval(projectId, [master.id]);
      if (rejected.length > 0) return false;
    }

    const targetMaster = master.personId === null;
    let donorId = master.personId;
    if (targetMaster) {
      const saved = this.facade.baseSettingsByProject()[projectId]?.photoBook?.masterCandidatePersonId ?? null;
      const donor = await this.facade.resolveTemplateDonorId(projectId, saved);
      if (!('donorId' in donor)) {
        showTemplateDonorRefusal(this.toast, donor.reason);
        return false;
      }

      donorId = donor.donorId;
      this.facade.markAlbumsRegenerating(projectId, [master.id]);
    }

    return this.facade.generateAlbum(
      projectId,
      { id: donorId as string },
      {
        structureFromProject: true,
        quiet: true,
        ...(targetMaster ? { targetMaster: true } : {}),
        ...(freshStructure ? { freshStructure } : {}),
        ...(resetPackId ? { resetDesignPack: resetPackId } : {}),
      },
    );
  }

  /**
   * The open or cached editor holds the OLD master model: hand it over before the student phase, never let it
   * save the stale one back. A failed rebuild may still have rewritten the model — the cache goes either way.
   */
  private async handOffMaster(
    projectId: string,
    masterId: string,
    built: boolean,
    curtain: RebuildCurtain | null,
    inMasterEditor: boolean,
    handoff: MasterHandoff,
  ): Promise<void> {
    if (curtain) {
      try {
        await curtain.reloadAlbum();
        handoff.curtainTaken = true;
      } catch {
        // The shell shows its own album-load error.
      }

      return;
    }

    // A reload now kills this client-side run: the students would never rebuild.
    if (!inMasterEditor) this.editorReuse.forget(projectId, masterId);
    else if (built) handoff.reloadAtEnd = masterId;
  }
}
